import type { HandoverKind, UserListStatus } from '@nest-recruit/shared';
import { LOCKED_TONE, USER_STATUS_TONES } from '../../../../components/status-tones';
import type { Tone } from '../../../../components/Tone';
import { EMPTY, formatDateTime } from '../../../../lib/format';
import type { OwnedRecordCounts, UserDetail, UserListRow } from '../../api/users-api';
import { ownedKinds } from './people-address';

// The words People uses, in one place so the tiles, the rows and the pane say the same thing.

export const PEOPLE_STATUS_LABELS: Readonly<Record<UserListStatus, string>> = {
  active: 'Active',
  invited: 'Invited',
  locked: 'Locked',
  suspended: 'Suspended',
  deactivated: 'Deactivated',
};

export const PEOPLE_STATUS_TONES: Readonly<Record<UserListStatus, Tone>> = {
  active: USER_STATUS_TONES.active,
  invited: USER_STATUS_TONES.invited,
  locked: LOCKED_TONE,
  suspended: USER_STATUS_TONES.suspended,
  deactivated: USER_STATUS_TONES.deactivated,
};

/**
 * "Roles and reach", second line (review R15): the server's own words for where their roles reach — "Whole
 * organisation" only for an organisation-wide role, else the ventures and departments — and — for no role at all.
 */
export function reachOf(person: Pick<UserListRow, 'reachLabel'>): string {
  return person.reachLabel ?? EMPTY;
}

type HasLock = Pick<UserListRow, 'status' | 'lockedUntil'>;

export function isLocked(person: HasLock, now: Date = new Date()): boolean {
  return person.status === 'active' && person.lockedUntil !== null && new Date(person.lockedUntil) > now;
}

/** The tile a person falls under: an active account inside its lockout window is Locked. */
export function listStatusOf(person: HasLock, now: Date = new Date()): UserListStatus {
  return isLocked(person, now) ? 'locked' : person.status;
}

/** "5 wrong passwords · until 27-10-2026 10:30 am GMT+6". */
export function lockLine(person: Pick<UserDetail, 'failedLoginCount' | 'lockedUntil'>): string {
  const count = person.failedLoginCount;
  const tries = `${count} wrong password${count === 1 ? '' : 's'}`;
  return person.lockedUntil === null ? tries : `${tries} · until ${formatDateTime(person.lockedUntil)}`;
}

/** "2 hire requests as recruiter, 1 job as line manager and 3 open tasks": exactly what a hand-over moves (review R4). */
export function describeOwned(owned: OwnedRecordCounts): string {
  const parts = ownedKinds(owned).map((kind) => countOf(kind, owned[kind]));
  if (parts.length === 0) return 'nothing';
  if (parts.length === 1) return parts[0] ?? 'nothing';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

const NOUNS: Readonly<Record<HandoverKind, readonly [string, string]>> = {
  // Only the requests they recruit for move; one they raised stays raised by them (review R4).
  requisitions: ['hire request as recruiter', 'hire requests as recruiter'],
  jobs: ['job as recruiter', 'jobs as recruiter'],
  lineManagedJobs: ['job as line manager', 'jobs as line manager'],
  tasks: ['open task', 'open tasks'],
};

export function countOf(kind: HandoverKind, count: number): string {
  const [one, many] = NOUNS[kind];
  return `${count} ${count === 1 ? one : many}`;
}

/** Enough of a user agent to recognise your own laptop, and no more. */
export function describeAgent(userAgent: string | null): string {
  if (!userAgent) return 'Unknown device';
  const browser = /Edg\//.test(userAgent)
    ? 'Edge'
    : /Chrome\//.test(userAgent)
      ? 'Chrome'
      : /Firefox\//.test(userAgent)
        ? 'Firefox'
        : /Safari\//.test(userAgent)
          ? 'Safari'
          : 'A browser';
  const device = /iPhone|Android/i.test(userAgent)
    ? /iPhone/i.test(userAgent)
      ? 'iPhone'
      : 'Android'
    : /iPad|Tablet/i.test(userAgent)
      ? 'a tablet'
      : /Windows/i.test(userAgent)
        ? 'Windows'
        : /Mac OS X|Macintosh/i.test(userAgent)
          ? 'Mac'
          : /Linux/i.test(userAgent)
            ? 'Linux'
            : null;
  return device === null ? browser : `${browser} on ${device}`;
}

/** The first name, for "View as Mizanur". */
export function firstNameOf(fullName: string): string {
  return fullName.split(' ')[0] ?? fullName;
}
